import type { EntitlementsValue } from '../../context/EntitlementsContext';
import { plansCopy } from '../../copy/plans';
import { fill } from './upgradeText';

// What the invite form may offer, from the plan's seats (spec 4.4). The
// server enforces the same limits on invite and on role change; this keeps
// the form from offering something that will fail. Until the plan is known,
// everything is offered, as before.

export type InviteRole = 'Executive' | 'ExecutiveAssistant';

export interface InviteRules {
  /** The EA role is on the plan at all. */
  offerEa: boolean;
  /** Why a role can't be picked right now (seats full), if it can't. */
  executiveFull: string | null;
  eaFull: string | null;
  /** "The EA seat comes with the Starter plan." when the plan has no EA seat. */
  eaLockedNote: string | null;
  /** Anyone can be added at all. */
  canInvite: boolean;
  /** Shown instead of the invite button when nobody can be added. */
  blockedMessage: string | null;
  /** The role a new invite starts on. */
  defaultRole: InviteRole;
}

export function inviteRules(ent: EntitlementsValue): InviteRules {
  const seats = ent.seats;
  if (ent.status !== 'ready' || !seats) {
    return {
      offerEa: true, executiveFull: null, eaFull: null, eaLockedNote: null,
      canInvite: true, blockedMessage: null, defaultRole: 'ExecutiveAssistant',
    };
  }

  const { seats: s } = plansCopy;
  const offerEa = ent.has('eaWorkspace');
  const eaPlan = ent.requiredPlan('eaWorkspace');
  const eaLockedNote = offerEa ? null : eaPlan ? fill(s.eaWithPlan, { plan: eaPlan }) : s.eaNotIncluded;
  // Zero means "no cap" (a custom contract).
  const executiveFull = seats.maxExecutiveSeats > 0 && seats.executivesUsed >= seats.maxExecutiveSeats
    ? fill(s.executivesFull, { max: seats.maxExecutiveSeats })
    : null;
  const eaFull = offerEa && seats.easUsed >= seats.maxEaSeats
    ? fill(s.easFull, { max: seats.maxEaSeats })
    : null;

  // A self-serve individual plan is one person by design.
  const canInvite = !ent.isSelfServeIndividual && (!executiveFull || (offerEa && !eaFull));
  const justYou = ent.isSelfServeIndividual || (seats.maxExecutiveSeats <= 1 && !offerEa);
  const blockedMessage = canInvite
    ? null
    : justYou
      ? [s.justYou, eaLockedNote].filter(Boolean).join(' ')
      : `${s.allFull} ${s.addSeats}`;

  return {
    offerEa,
    executiveFull,
    eaFull,
    eaLockedNote,
    canInvite,
    blockedMessage,
    defaultRole: offerEa && !eaFull ? 'ExecutiveAssistant' : 'Executive',
  };
}
